import { StyleSheet, Document, Page, Text, View, Image } from '@react-pdf/renderer'
import React from 'react'
import HeaderOfPDFFashion from './Header'
import FooterOfPDFFashion from './Footer'
import MainFieldsOfPDF from '../MainFields'
import { SectionTitle } from '../SectionTitle'
import TableOfPDF from '../Table'
import type { FashionOpPdfData } from './fashionOpPdfTypes'

const styles = StyleSheet.create({
  page: {
    fontFamily: 'Helvetica',
    paddingTop: 122,
    paddingBottom: 100,
    paddingHorizontal: 40,
  },
  pageNumber: {
    position: 'absolute',
    top: 36,
    left: 0,
    right: 0,
    textAlign: 'center',
    fontSize: 9,
    color: '#000',
  },
  pageTitle: {
    position: 'absolute',
    top: 76,
    left: 40,
    right: 40,
    textAlign: 'center',
    fontSize: 13,
    fontWeight: 'bold',
    color: '#000',
  },
  section: {
    marginBottom: 16,
    paddingBottom: 10,
    borderBottom: '1px solid #000',
  },
  itemRow: {
    marginBottom: 8,
    paddingHorizontal: 12,
    paddingVertical: 6,
    backgroundColor: '#f5f5f5',
    borderRadius: 4,
    border: '1px solid #000',
  },
  label: {
    fontWeight: 'bold',
    marginBottom: 3,
    fontSize: 9,
    color: '#000',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  value: {
    marginBottom: 4,
    fontSize: 10,
    color: '#000',
    lineHeight: 1.3,
  },
  textBlock: {
    fontSize: 10,
    color: '#000',
    lineHeight: 1.4,
    marginBottom: 6,
  },
  noteRow: {
    marginBottom: 8,
    paddingHorizontal: 10,
    paddingVertical: 6,
    backgroundColor: '#f5f5f5',
    border: '1px solid #000',
    borderRadius: 3,
  },
  noteSector: {
    fontSize: 9,
    fontWeight: 'bold',
    marginBottom: 3,
  },
  noteText: {
    fontSize: 9,
    lineHeight: 1.35,
  },
  noteImagesRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginTop: 6,
    gap: 4,
  },
  noteImage: {
    width: 120,
    height: 120,
    objectFit: 'contain',
    borderRadius: 2,
    border: '1px solid #ccc',
  },
  productImageSection: {
    marginBottom: 16,
    paddingBottom: 10,
    borderBottom: '1px solid #000',
    alignItems: 'center',
  },
  productImage: {
    width: 180,
    height: 180,
    objectFit: 'contain',
  },
})

interface BodyOfPDFFashionType {
  data?: FashionOpPdfData | null
  qrCodeDataUrl?: string
}

const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return '—'
  try {
    return new Date(dateString).toLocaleDateString('pt-BR')
  } catch {
    return '—'
  }
}

const formatQuantity = (value: number): string => {
  if (!Number.isFinite(value)) return '—'
  const rounded = Math.round(value * 1000) / 1000
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(3).replace(/\.?0+$/, '')
}

const getStatusLabel = (status: string): string => {
  const statusMap: Record<string, string> = {
    pending: 'Pendente',
    iniciado: 'Iniciado',
    in_progress: 'Em Progresso',
    completed: 'Concluído',
    cancelled: 'Cancelado',
  }
  return statusMap[status] || status
}

const BodyOfPDFFashion: React.FC<BodyOfPDFFashionType> = ({ data, qrCodeDataUrl }) => {
  const pushFields = (): { label: string; subLabel?: string; value: string }[] => {
    const fields: { label: string; subLabel?: string; value: string }[] = []

    if (data?.status) {
      fields.push({ label: 'Status', value: getStatusLabel(data.status) })
    }
    if (data?.fashionProduct?.name) {
      fields.push({ label: 'Produto', value: data.fashionProduct.name })
    }
    if (data?.color?.name) {
      fields.push({ label: 'Cor', value: data.color.name })
    }
    if (data?.totalItems) {
      fields.push({ label: 'Quantidade de Itens', value: String(data.totalItems) })
    }
    if (data?.cutter?.name) {
      fields.push({ label: 'Cortador', value: data.cutter.name })
    }
    if (data?.dressmaker?.name) {
      fields.push({ label: 'Costureira', value: data.dressmaker.name })
    }

    return fields
  }

  const sizeColumns = data?.sizeColumns ?? []
  const sizeTableHeaders = ['Pedido', 'Cliente', ...sizeColumns, 'Total']

  const sizeTableData =
    data?.orders?.map((order) => {
      const rowTotal =
        sizeColumns.length > 0
          ? sizeColumns.reduce((sum, size) => sum + (order.sizeQuantities[size] ?? 0), 0)
          : order.quantityForThisProduction

      return [
        { value: `PED-${order.fashionOrderId}` },
        { value: order.clientName || '—' },
        ...sizeColumns.map((size) => ({
          value: order.sizeQuantities[size] ? String(order.sizeQuantities[size]) : '—',
        })),
        { value: String(rowTotal) },
      ]
    }) ?? []

  const consumptionHeaders = ['Tipo', 'Material / Insumo', 'Quantidade', 'Unidade']
  const consumptionTableData =
    data?.consumption?.map((row) => [
      { value: row.kind === 'material' ? 'MP' : 'Insumo' },
      { value: row.name },
      { value: formatQuantity(row.quantity) },
      { value: row.unit || '—' },
    ]) ?? []

  const observations = data?.fashionProduct?.observations?.trim() ?? ''
  const hasOrders = (data?.orders?.length ?? 0) > 0
  const hasSizeTable = sizeTableData.length > 0
  const hasNotes = (data?.beneficiamentoNotes?.length ?? 0) > 0
  const hasConsumption = consumptionTableData.length > 0

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <HeaderOfPDFFashion />
        <Text
          fixed
          style={styles.pageNumber}
          render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`}
        />
        <Text fixed style={styles.pageTitle}>{`OP-${data?.id || ''}`}</Text>

        <MainFieldsOfPDF infoFields={pushFields()} />

        {data?.fashionProduct?.coverImageUrl && (
          <View style={styles.productImageSection} wrap={false}>
            <SectionTitle title="Imagem do Produto" />
            <Image src={data.fashionProduct.coverImageUrl} style={styles.productImage} />
          </View>
        )}

        {hasOrders && (
        <View style={styles.section}>
          <SectionTitle title="Pedidos" />
          {data?.orders?.map((order) => (
              <View key={order.fashionOrderId} style={styles.itemRow} wrap={false}>
                <Text style={styles.value}>
                  <Text style={styles.label}>Pedido: </Text>
                  PED-{order.fashionOrderId}
                </Text>
                <Text style={styles.value}>
                  <Text style={styles.label}>Cliente: </Text>
                  {order.clientName || '—'}
                </Text>
                <Text style={styles.value}>
                  <Text style={styles.label}>Data início: </Text>
                  {formatDate(order.startDateOfDelivery)}
                </Text>
                <Text style={styles.value}>
                  <Text style={styles.label}>Data fim: </Text>
                  {formatDate(order.finalDateOfDelivery)}
                </Text>
              </View>
            ))}
        </View>
        )}

        {hasSizeTable && (
        <View style={styles.section}>
          <SectionTitle title="Quantidades por tamanho" />
          <TableOfPDF tableHeaders={sizeTableHeaders} tableData={sizeTableData} />
        </View>
        )}

        {observations && (
        <View style={styles.section}>
          <SectionTitle title="Observação" />
          <Text style={styles.textBlock}>{observations}</Text>
        </View>
        )}

        {hasNotes && (
        <View style={styles.section}>
          <SectionTitle title="Anotações" />
          {data?.beneficiamentoNotes?.map((note, index) => (
              <View key={index} style={styles.noteRow} wrap={false}>
                <Text style={styles.noteSector}>{note.sectorName}</Text>
                <Text style={styles.noteText}>{note.description?.trim() || '—'}</Text>
                {note.imageUrls && note.imageUrls.length > 0 && (
                  <View style={styles.noteImagesRow}>
                    {note.imageUrls.map((url, imgIndex) => (
                      <Image key={imgIndex} src={url} style={styles.noteImage} />
                    ))}
                  </View>
                )}
              </View>
            ))}
        </View>
        )}

        {hasConsumption && (
        <View style={styles.section} wrap={false}>
          <SectionTitle title="Consumo de MP e Insumos" />
          <TableOfPDF tableHeaders={consumptionHeaders} tableData={consumptionTableData} />
        </View>
        )}

        <FooterOfPDFFashion qrCodeDataUrl={qrCodeDataUrl} qrCodeValue={data?.id?.toString()} />
      </Page>
    </Document>
  )
}

export default BodyOfPDFFashion
